import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../api/api';

const FormasPago = () => {
  const [formasPago, setFormasPago] = useState([]);
  const [originalFormasPago, setOriginalFormasPago] = useState([]);
  const [pets, setPets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFormaPago, setSelectedFormaPago] = useState(null);
  const [formData, setFormData] = useState({
    id_pet: '',
    desc_forma_pago: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchFormasPago();
    fetchPets();
  }, []);

  const fetchFormasPago = async () => {
    setLoading(true);
    try {
      // 💡 Se añade la barra final '/' para evitar 404 en FastAPI
      const response = await api.get('/formas-pago/');
      setFormasPago(response.data || []);
      setOriginalFormasPago(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error al obtener formas de pago:', err);
      setError('Error al cargar las formas de pago');
      setSnackbarMessage('Error al cargar las formas de pago');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPets = async () => {
    try {
      // 💡 Se añade la barra final '/' tal como en Especies.js
      const response = await api.get('/pets/');
      setPets(response.data || []);
    } catch (err) {
      console.error('Error al obtener la lista de veterinarias/pets:', err);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setPage(0);

    if (value.trim() === '') {
      setFormasPago(originalFormasPago);
    } else {
      const filtered = originalFormasPago.filter((item) =>
        (item.desc_forma_pago || '').toLowerCase().includes(value)
      );
      setFormasPago(filtered);
    }
  };

  const handleOpenModal = (formaPago = null) => {
    if (formaPago) {
      setSelectedFormaPago(formaPago);
      setFormData({
        id_pet: formaPago.id_pet || '',
        desc_forma_pago: formaPago.desc_forma_pago || '',
      });
    } else {
      setSelectedFormaPago(null);
      setFormData({
        id_pet: pets.length > 0 ? pets[0].id_pet : '',
        desc_forma_pago: '',
      });
    }
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      if (selectedFormaPago) {
        // 💡 Barra final en PUT
        await api.put(`/formas-pago/${selectedFormaPago.id_forma_pago}`, formData);
        setSnackbarMessage('Forma de pago actualizada correctamente');
      } else {
        // 💡 Barra final en POST -> '/formas-pago/'
        await api.post('/formas-pago/', formData);
        setSnackbarMessage('Forma de pago registrada correctamente');
      }
      setOpenModal(false);
      fetchFormasPago();
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error al guardar forma de pago:', err);
      setError('Error al guardar la forma de pago');
      setSnackbarMessage(err.response?.data?.detail || 'Error al guardar la forma de pago');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta forma de pago?')) {
      try {
        await api.delete(`/formas-pago/${id}`);
        setSnackbarMessage('Forma de pago eliminada correctamente');
        setSnackbarOpen(true);
        fetchFormasPago();
      } catch (err) {
        console.error('Error al eliminar forma de pago:', err);
        setError('Error al eliminar la forma de pago');
        setSnackbarMessage('Error al eliminar la forma de pago');
        setSnackbarOpen(true);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gestión de Formas de Pago
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Nueva Forma de Pago
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ my: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por descripción..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Descripción Forma de Pago</TableCell>
                <TableCell>Veterinaria / Pet</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formasPago
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => {
                  const petAsociada = pets.find((p) => p.id_pet === item.id_pet);
                  return (
                    <TableRow key={item.id_forma_pago}>
                      <TableCell>{item.id_forma_pago}</TableCell>
                      <TableCell>{item.desc_forma_pago}</TableCell>
                      <TableCell>
                        {petAsociada ? petAsociada.nombre_veterinaria || petAsociada.nombre : item.id_pet}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenModal(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        {/* ===================== Eliminar===================== 
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.id_forma_pago)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        */}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {formasPago.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No se encontraron formas de pago.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de Crear / Editar */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedFormaPago ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Descripción de la Forma de Pago"
            variant="outlined"
            value={formData.desc_forma_pago}
            onChange={(e) => setFormData({ ...formData, desc_forma_pago: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          {pets.length > 0 && (
            <TextField
              select
              fullWidth
              label="Veterinaria / Pet"
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.map((pet) => (
                <MenuItem key={pet.id_pet} value={pet.id_pet}>
                  {pet.nombre_veterinaria || pet.nombre || `Pet #${pet.id_pet}`}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.desc_forma_pago.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificaciones centradas como en Especies.js */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1500 }}
      >
        <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FormasPago;